import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  defaultIfEmpty,
  distinctUntilChanged,
  map,
  Observable,
  skipWhile,
  Subscription,
  take
} from 'rxjs';
import { ApplicationStateService } from '../services/application.state.service';
import {CustomAttributesFilter, FilterService, GenericFilter} from '../services/filter/filter.service';
import { preloadImages } from '../utils/image-preload';
import { DoorType } from '../models/door';
import { TrackingService } from '../services/tracking.service';
import { LoadingComponent } from '../models/loading.component';
import { DoorService } from '../services/door/door.service';
import { CustomerService } from '../services/customer/customer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Product, ProductType } from '../models/product';
import { TranslateService } from '@ngx-translate/core';
import { AppView } from '../models/app.view';
import {DoorTypeValidationService} from "../services/doorTypeValidation/door-type-validation.service";

export type LoadedProduct = Product & { isLoaded?: boolean };

export type Props = {
  products: Array<LoadedProduct>;
  selected: Product | undefined;
}

// component just to satisfy angular compiler
@Component({
  template: ''
})
export abstract class AbstractDoorPanelComponent implements OnInit, OnDestroy, LoadingComponent {

  subscriptions: Array<Subscription> = [];

  isWidgetMode$ = this.route.data.pipe(map(data => data['viewMode'] === AppView.Widget));
  products$ = this.doorService.getProducts$();
  lastProducts$ = new BehaviorSubject<Map<string, LoadedProduct> | undefined>(undefined);
  isFavoriteFeatureEnabled$ = combineLatest([
    this.customerService.getCustomerConfig$().pipe(map(config => config.isFavoriteFeatureEnabled)),
    this.isWidgetMode$
  ]).pipe(map(([isFavoriteFeatureEnabled, isWidget]) => isFavoriteFeatureEnabled && !isWidget));

  enableDoorTypeDropdown$ = this.customerService.getCustomerConfig$().pipe(map(config => !!config.enableDoorTypeDropdownSelection))

  mainDoorTypeFilter$ = this.customerService.getCustomerConfig$().pipe(map(config => config.mainDoorTypeFilter));

  hasCustomAttributesFilter$ = combineLatest([
    this.customerService.getCustomerConfig$().pipe(map(config => config.showCustomAttributesFilter)),
    this.filterService.getFilterOptions$().pipe(map(filterOption => filterOption?.customAttributesFilterOptions && filterOption.customAttributesFilterOptions.length > 0))
  ]).pipe(map(([showCustomAttributesFilter, hasOptions]) => !!showCustomAttributesFilter && hasOptions));


  isDoorTypeFilterEnabled$ = combineLatest([
    this.mainDoorTypeFilter$,
    this.isWidgetMode$,
  ]).pipe(map(([mainDoorTypeFilter, isWidgetMode]) => mainDoorTypeFilter !== undefined && mainDoorTypeFilter.length > 0 && !isWidgetMode));

  isFilterFeatureEnabled$ = combineLatest([this.filterService.isFilterFeatureEnabled$(), this.isWidgetMode$]).pipe(
    map(([showFilter, isWidget]) => showFilter && !isWidget),
    defaultIfEmpty(false)
  );

  productTypeFilter$ = this.customerService.getCustomerConfig$().pipe(map(config => config.supportedProductTypes));

  productTypeFilterWithoutDoor$ = this.productTypeFilter$.pipe(map(types => types ? types.filter(type => type !== 'door') : undefined));

  areProductsEnabled$ = combineLatest([
    this.customerService.getCustomerConfig$().pipe(map(config => config.enabledProducts)),
    this.isWidgetMode$
  ]).pipe(map(([enabledProducts, isWidget]) => enabledProducts && !isWidget));

  hasAtLeastOneFilter$ = combineLatest([
    this.mainDoorTypeFilter$,
    this.productTypeFilter$
  ]).pipe(
    map(([mainDoorTypes, productTypes]) => {
      // Handle undefined values and check if any array has at least one item
      return (mainDoorTypes && mainDoorTypes.length > 0);
    })
  );

  props$: Observable<Props> = combineLatest([
    this.products$,
    this.doorService.getSelectedDoorAtCurrentIndex$(),
    // this.scrollPosition
  ]).pipe(map(([products, selectedDoor]) => {
    const lastProducts = this.lastProducts$.getValue();
    const result = {
      products: products.map(d => {
        return Object.assign(d, { isLoaded: lastProducts?.get(d.id)?.isLoaded });
      }),
      selected: selectedDoor
    };
    this.lastProducts$.next(new Map(result.products.map(product => [product.id, product])));
    return result;
  }));

  isLoaded$ = new BehaviorSubject(false);
  showDoorLoadingIndicator$ = this.doorService.isLoading$.pipe(distinctUntilChanged(), debounceTime(300));

  favoritesCount$ = this.doorService.getFavoritesCount();

  showFilter$ = this.applicationStateService.getShowFilter();

  isFavoriteFilterEnabled$ = this.filterService.getOnlyShowFavorites$();
  sortingOptions$ = this.filterService.getSortingOptions$();
  isSortingActive$ = this.sortingOptions$.pipe(map(options => options.length > 0));
  selectedSorting$ = this.filterService.getSelectedSorting$();

  shouldShowNoFavorites$ = combineLatest([this.isFavoriteFilterEnabled$, this.favoritesCount$])
    .pipe(map(([isEnabled, count]) => isEnabled && count === 0));

  shouldShowNoProductsWarning$ = combineLatest([this.products$, this.doorService.isLoading$])
    .pipe(map(([products, isLoading]) => !isLoading && products.length === 0));


  public currentSelectedDoorType: DoorType | undefined;
  public currentSelectedProductType$: Observable<ProductType> = this.filterService.getFilter$().pipe(map(filter => filter?.selectedProductType || 'door'));

  constructor(
    public doorService: DoorService,
    public applicationStateService: ApplicationStateService,
    protected customerService: CustomerService,
    protected filterService: FilterService<GenericFilter>,
    protected trackingService: TrackingService,
    protected router: Router,
    protected doorTypeValidationService: DoorTypeValidationService,
    protected route: ActivatedRoute,
    protected translationService: TranslateService
  ) {
    this.applicationStateService.register(this)
  }

  translatedCurrentlySelectedProductType$ = this.currentSelectedProductType$.pipe(map(type => this.translationService.instant(`productType.${type}`)));

  ngOnInit(): void {
    // subscribe to visual doors and trigger preload of images
    this.subscriptions.push(this.products$.pipe(skipWhile(products => products.length === 0), take(1)).subscribe({
      next: products => {
        if (products.length > 0) {
          preloadImages(products.filter(i => !!i.thumbnailUrl).map(i => i.thumbnailUrl) as Array<string>).subscribe({
            next: v => this.isLoaded$.next(v),
            error: (error) => {
              this.router.navigateByUrl('error').then();
            }
          });
        }
      }
    }));
    this.filterService.loadSortingOptions();

    this.subscriptions.push(this.customerService.getCustomerConfig$().subscribe(async config => {
      this.filterService.getFilter$().pipe().subscribe({
        next: filter => {
          this.currentSelectedDoorType = filter?.selectedDoorType || config.defaultDoorType;
        }
      });
    }));
  }

  public onCustomAttributeFilterChanged(filters: Array<CustomAttributesFilter> | null): void {
    this.filterService.setFilter({ customAttributes: filters });
  }

  public isProductSelected(product: Product): Observable<boolean> {

    if (product.productType === 'door') {
      return this.doorService.getSelectedDoorAtCurrentIndex$().pipe(map(selected => {
        return !!selected && selected.id === product.id;
      }));
    } else {
      return this.doorService.getSelected$().pipe(map(selected => {

        const currentIndex = this.doorService.getSelectedDoorCornersIndexValue();

        const products = selected.get(currentIndex);

        if (!products) {
          return false;
        }
        return !!products.products?.find(p => p.id === product.id);
      }));
    }
  }

  onScroll() {
    this.doorService.loadNextPage();
    // this.scrollPosition.next(this.scrollPosition.getValue() + 1);
  }

  onProductLoaded(product: LoadedProduct): void {
    product.isLoaded = true;
  }

  onProductSelected(product: LoadedProduct): void {
    this.trackingService.track({
      event: 'selectDoor',
      body: { selectedDoorId: product.id, selectedExternalDoorId: product.externalIds }
    }).then();

    this.doorTypeValidationService.validateNewProductSelection(product).then(result => {
      if (result) {
        this.doorService.selectProductAtCurrentIndex(product);
      }
    })
  }

  async onToggleFavorite(product: LoadedProduct, event: Event): Promise<void> {
    this.doorService.toggleFavorite(product);
    this.trackingService.track({
      event: 'toggleFavorite',
      body: {
        selectedDoorId: product.id,
        selectedExternalDoorId: product.externalIds,
        isFavorite: product.isFavorite,
        location: 'doorHover'
      }
    }).then();

    event.stopPropagation();
  }

  getArrayOfLoadingDoors(count = 8): Array<number> {
    return [...Array(count).keys()];
  }

  getArrayOfLoadingDoorsForProgress(count = 6): Array<number> {
    return [...Array(count).keys()];
  }

  onSetFilter(event: Event): void {
    // for mobile touchstart events - ios issue
    event.stopPropagation();
    event.preventDefault();
    this.applicationStateService.setShowFilter(true);
  }

  onSelectSorting(sortingId: string) {
    this.filterService.setFilter({
      selectedSortingOptionId: sortingId || undefined // map empty string to undefined
    });
  }

  isSelectedSorting(sortingId: string | undefined): Observable<boolean> {
    return this.selectedSorting$.pipe(map(s => s === (sortingId || undefined)));
  }

  async setMainDoorTypeFilter(doorType: DoorType): Promise<void> {
    const shouldChange = await this.doorTypeValidationService.validateDoorTypeFilterChange(doorType)

    if (!shouldChange) {
      return
    }

    const newProductTypeSelection = 'door';

    this.filterService.setFilter({ selectedDoorType: doorType, selectedProductType: newProductTypeSelection });
  }

  setProductTypeFilter(productType: ProductType): void {
    this.filterService.setFilter({ selectedProductType: productType, customAttributes: null });
  }

  doToggleShowFavorites(checked: boolean): void {
    this.filterService.setOnlyShowFavorites(checked);

    this.favoritesCount$.pipe(take(1)).subscribe(count => {
      this.trackingService.track({
        event: 'toggleFilterForFavorites',
        body: { currentFavoriteCount: count, filterForFavorites: checked }
      }).then();
    });
  }

  onToggleFavoriteFilter(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    this.doToggleShowFavorites(input.checked);
  }

  onToggleFavoriteFilterMobile(event: Event): void {
    // for mobile touchstart events - ios issue
    event.stopPropagation();
    event.preventDefault();
    this.doToggleShowFavorites(!this.filterService.getOnlyShowFavorites());
  }

  productsTrackBy(index: number, item: LoadedProduct): string {
    return item.id;
  }

  isLoaded(): Observable<boolean> {
    return this.isLoaded$.pipe(distinctUntilChanged());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.applicationStateService.unregister(this);
  }
}
